import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPaymentsPlatformV1CheckoutSession from './payments-platform-v1-checkout-session-checkout-sessions.http.js';

/** A Wix-hosted checkout page generated for a specific set of line items. */
export interface CheckoutSession {
  /**
   * Checkout session ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * URL of the Wix-hosted checkout page where the buyer completes payment. The buyer enters the page
   * as an anonymous shopper, and any session held by the calling app isn't carried over.
   *
   * The URL carries query parameters Wix needs to render the page correctly (for example, a
   * `successUrl` parameter derived from `callbackUrls.thankYouPageUrl`). Pass the URL through to the
   * buyer unchanged. Don't strip, reorder, rewrite, or otherwise mutate its query string.
   * @readonly
   * @format WEB_URL
   */
  redirectUrl?: string | null;
}

/** Request to create a checkout session. */
export interface GenerateCheckoutSessionRequest {
  /** Items, quantities, and prices the buyer is paying for, plus optional pre-fill customer details. */
  cart: Cart;
  /** URLs the buyer is redirected to after completing or leaving the checkout flow. */
  callbackUrls: CallbackUrls;
}

/**
 * Recurring subscription configuration for a line item.
 *
 * `LineItem.name` and `subscriptionInfo.title`/`description` render in different parts of the
 * Wix-hosted checkout page: `LineItem.name` is the line label in the cart-summary list, while
 * `subscriptionInfo.title` and `subscriptionInfo.description` are the heading and body text on the
 * recurring-billing summary block (the area that shows the buyer the cadence and the free-trial terms,
 * if any). Set them to complementary values rather than duplicating `LineItem.name`.
 */
export interface SubscriptionInfo {
  /**
   * Billing frequency, cycles, and free trial configuration. Required when `subscriptionInfo` is set on a
   * line item.
   */
  subscriptionSettings?: SubscriptionSettings;
  /**
   * Subscription name shown to the buyer in the recurring-billing summary block on the Wix-hosted
   * checkout page (the heading of that block).
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Subscription description shown to the buyer in the recurring-billing summary block on the
   * Wix-hosted checkout page (the body text below the title).
   * @maxLength 500
   */
  description?: string | null;
}

/**
 * Configuration for recurring subscription charges.
 *
 * `autoRenewal` and `billingCycles` are mutually exclusive. `autoRenewal: true` describes an
 * open-ended subscription that renews indefinitely, while `billingCycles: N` describes a fixed-term
 * subscription that stops after N charges. Setting `autoRenewal: true` together with any
 * `billingCycles` value is rejected with `INVALID_ARGUMENT` (application code
 * `AUTO_RENEWAL_AND_BILLING_CYCLES_CONFLICT`).
 */
export interface SubscriptionSettings {
  /** Billing frequency unit. Required when `subscriptionSettings` is set; `UNDEFINED` is rejected. */
  frequency?: SubscriptionFrequencyWithLiterals;
  /**
   * Number of frequency units between charges. For `DAY` frequency, the minimum is 7. The shortest
   * billing cadence supported is weekly; values 1–6 with `frequency: DAY` are rejected with
   * `INVALID_ARGUMENT` (rule code `MIN_VALUE`). Default: `1`.
   *
   * Note that `freeTrialPeriod.interval` doesn't share this floor: a `DAY` trial may have
   * `interval` as low as 1.
   * @min 1
   * @max 3650
   */
  interval?: number | null;
  /**
   * Whether the subscription renews automatically. Can't be `true` when `billingCycles` is set.
   *
   * Default: `true` when both `autoRenewal` and `billingCycles` are omitted; `false` when `billingCycles`
   * is set and `autoRenewal` is omitted.
   */
  autoRenewal?: boolean | null;
  /**
   * Number of billing cycles before the subscription stops. Can't be set when `autoRenewal` is `true`.
   *
   * When omitted along with `autoRenewal`, the subscription renews indefinitely.
   * @min 1
   */
  billingCycles?: number | null;
  /** Free trial period before the first charge. */
  freeTrialPeriod?: FreeTrialPeriod;
}

/** Frequency unit for subscription billing and trial periods. */
export enum SubscriptionFrequency {
  /** Unknown frequency. */
  UNDEFINED = 'UNDEFINED',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type SubscriptionFrequencyWithLiterals =
  | SubscriptionFrequency
  | 'UNDEFINED'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

/**
 * Free trial period before the first subscription charge.
 *
 * The `frequency` enum is shared with `SubscriptionSettings.frequency` for convenience, but here it
 * describes a one-off duration unit (the length of the trial), not a recurring billing cadence. A
 * `frequency: MONTH` + `interval: 3` trial means "a single 3-month trial," not "billed every 3
 * months."
 */
export interface FreeTrialPeriod {
  /**
   * Trial period frequency unit. Required when `freeTrialPeriod` is set; `UNDEFINED` should be
   * treated as a client mistake and isn't a valid runtime value.
   */
  frequency?: SubscriptionFrequencyWithLiterals;
  /**
   * Length of the trial period in `frequency` units. For example, `interval: 14` with `frequency: DAY`
   * gives a 14-day trial.
   *
   * Unlike `SubscriptionSettings.interval`, this field's `DAY` floor is `1`. No weekly floor applies
   * to the trial length. Values ≤ 0 are rejected with `INVALID_ARGUMENT` (rule code
   * `MIN_VALUE`).
   * @min 1
   * @max 999
   */
  interval?: number;
}

export interface PhysicalProperties {
  /** Whether the item requires shipping. */
  shippable?: boolean | null;
}

export interface LineItem {
  /**
   * Name of the item, shown on the checkout page.
   * @minLength 1
   * @maxLength 255
   */
  name?: string | null;
  /**
   * Quantity of this item to charge for.
   * @min 1
   */
  quantity?: number | null;
  /**
   * Per-unit price as a decimal string in the merchant account currency. For example, `"19.99"`.
   * @minLength 1
   * @maxLength 50
   */
  price?: string | null;
  /**
   * Subscription configuration. When set, the line item follows a scheduled-billing model defined by
   * `subscriptionSettings` instead of a single immediate charge. The recurring charge each billing
   * cycle is `price × quantity`, and `quantity` is locked at session creation.
   *
   * At most 1 line item per request may set `subscriptionInfo`. Wix rejects requests with 2 or more
   * subscription line items with `MULTIPLE_SUBSCRIPTION_ITEMS`.
   */
  subscriptionInfo?: SubscriptionInfo;
  /** Optional. Physical properties of the item, used when the item requires shipping. */
  physicalProperties?: PhysicalProperties;
}

/**
 * Buyer details used to pre-fill the checkout form. The buyer can edit any of these fields on the
 * Wix checkout page.
 */
export interface CustomerInfo {
  /**
   * Buyer's first name.
   * @maxLength 100
   */
  firstName?: string | null;
  /**
   * Buyer's last name.
   * @maxLength 100
   */
  lastName?: string | null;
  /**
   * Buyer's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /**
   * Buyer's email address.
   * @format EMAIL
   */
  email?: string | null;
}

/** Items, quantities, and prices the buyer is paying for, plus optional pre-fill customer details. */
export interface Cart {
  /**
   * Line items the buyer is paying for in this checkout session.
   *
   * At most 1 subscription line item (an item with `subscriptionInfo` set) is allowed per request.
   * Wix rejects requests with 2 or more subscription items. Non-recurring items can be combined with a
   * single subscription item. In that case the buyer is charged once at checkout for the non-recurring
   * items and, if applicable, the first cycle of the subscription. Wix bills any subsequent cycles on
   * the subscription's own schedule.
   * @maxSize 100
   */
  items?: LineItem[];
  /**
   * Buyer details used to pre-fill the checkout form. The buyer can edit any of these fields on the
   * Wix checkout page.
   */
  customerInfo?: CustomerInfo;
}

/**
 * URLs the buyer is redirected to after the checkout flow.
 *
 * The buyer is sent to one of the URLs below: `continueBrowsingUrl` if they explicitly leave via
 * **Continue browsing**, or `thankYouPageUrl` after a successful payment.
 *
 * If the buyer abandons the page implicitly (closing the tab, navigating away, network failure), no
 * callback URL is invoked and the checkout session isn't resumed.
 */
export interface CallbackUrls {
  /**
   * URL the buyer is redirected to from the Wix checkout page when they choose to leave without
   * completing payment, either when they click **Continue browsing** or when they navigate back to
   * a cart-style page.
   *
   * Deprecated. Use `continueBrowsingUrl` instead.
   * @format WEB_URL
   * @deprecated
   * @replacedBy continue_browsing_url
   */
  postFlowUrl?: string | null;
  /**
   * URL the buyer is redirected to after a successful payment.
   * @format WEB_URL
   */
  thankYouPageUrl?: string | null;
  /**
   * URL the buyer is redirected to from the Wix checkout page when they choose to leave without
   * completing payment, either when they click **Continue browsing** or when they navigate back to
   * a cart-style page.
   * @format WEB_URL
   */
  continueBrowsingUrl?: string | null;
}

/** Response after creating a checkout session. */
export interface GenerateCheckoutSessionResponse {
  /** The created checkout session. */
  checkoutSession?: CheckoutSession;
}

/** Request to register a webhook URL. */
export interface RegisterCheckoutSessionWebhookRequest {
  /**
   * URL to receive webhook notifications.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Webhook event types to register. Default: `[ORDER_APPROVED]`.
   * @maxSize 10
   */
  webhookTypes?: WebhookTypeWithLiterals[];
}

/** Webhook event types to register. */
export enum WebhookType {
  /** Order Approved webhook (`wix.ecom.v1.order_approved`). */
  ORDER_APPROVED = 'ORDER_APPROVED',
  /**
   * Subscription Contract Canceled webhook
   * (`wix.ecom.subscription_contracts.v1.subscription_contract_canceled`).
   */
  SUBSCRIPTION_CANCELED = 'SUBSCRIPTION_CANCELED',
  /**
   * Subscription Contract Expired webhook
   * (`wix.ecom.subscription_contracts.v1.subscription_contract_expired`).
   */
  SUBSCRIPTION_ENDED = 'SUBSCRIPTION_ENDED',
}

/** @enumType */
export type WebhookTypeWithLiterals =
  | WebhookType
  | 'ORDER_APPROVED'
  | 'SUBSCRIPTION_CANCELED'
  | 'SUBSCRIPTION_ENDED';

/** Response after registering a webhook. */
export interface RegisterCheckoutSessionWebhookResponse {
  /**
   * PEM-formatted RS256 public key used to verify the JWT signature on incoming webhook payloads.
   * @maxLength 1000
   */
  publicKey?: string | null;
}

/** @docsIgnore */
export type GenerateCheckoutSessionApplicationErrors =
  | {
      code?: 'MULTIPLE_SUBSCRIPTION_ITEMS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'AUTO_RENEWAL_AND_BILLING_CYCLES_CONFLICT';
      description?: string;
      data?: Record<string, any>;
    };

/**
 * Creates a Wix-hosted checkout session for the supplied line items and returns its redirect URL
 * and session ID.
 *
 * Each call creates a new, independent session. The method isn't idempotent, so a retry produces
 * an additional session rather than recovering the prior one.
 * @param cart - Items, quantities, and prices the buyer is paying for, plus optional pre-fill customer details.
 * @public
 * @requiredField cart
 * @requiredField cart.items
 * @requiredField cart.items.name
 * @requiredField cart.items.price
 * @requiredField cart.items.quantity
 * @requiredField cart.items.subscriptionInfo.subscriptionSettings
 * @requiredField cart.items.subscriptionInfo.subscriptionSettings.freeTrialPeriod.frequency
 * @requiredField cart.items.subscriptionInfo.subscriptionSettings.frequency
 * @requiredField options
 * @requiredField options.callbackUrls
 * @requiredField options.callbackUrls.thankYouPageUrl
 * @permissionId payments:platform:v1:checkout_session:generate_checkout_session
 * @permissionId payments:platform:v1:checkout_session:construct_checkout_session
 * @applicableIdentity APP
 * @returns Response after creating a checkout session.
 * @fqn wix.payments.platform.v1.CheckoutSessions.GenerateCheckoutSession
 */
export async function generateCheckoutSession(
  cart: NonNullablePaths<
    Cart,
    | `items`
    | `items.${number}.name`
    | `items.${number}.price`
    | `items.${number}.quantity`
    | `items.${number}.subscriptionInfo.subscriptionSettings`
    | `items.${number}.subscriptionInfo.subscriptionSettings.freeTrialPeriod.frequency`
    | `items.${number}.subscriptionInfo.subscriptionSettings.frequency`,
    7
  >,
  options: NonNullablePaths<
    GenerateCheckoutSessionOptions,
    `callbackUrls` | `callbackUrls.thankYouPageUrl`,
    3
  >
): Promise<
  GenerateCheckoutSessionResponse & {
    __applicationErrorsType?: GenerateCheckoutSessionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    cart: cart,
    callbackUrls: options?.callbackUrls,
  });

  const reqOpts =
    ambassadorWixPaymentsPlatformV1CheckoutSession.generateCheckoutSession(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          cart: '$[0]',
          callbackUrls: '$[1].callbackUrls',
        },
        singleArgumentUnchanged: false,
      },
      ['cart', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GenerateCheckoutSessionOptions {
  /** URLs the buyer is redirected to after completing or leaving the checkout flow. */
  callbackUrls: CallbackUrls;
}
